import { useCallback, useState } from 'react';

export type GapReviewTheme = 'dark' | 'light';

const THEME_STORAGE_KEY = 'gap-review-theme';

/**
 * The theme is read once, when this module is first imported — which for a
 * lazily loaded route is before its first render, so `.gr-root` mounts with the
 * right `data-theme` and light mode never flashes dark.
 *
 * `localStorage` can throw outright (Safari private browsing), and this runs at
 * module scope, so a throw here would take the whole route chunk down. Falling
 * back to the OS preference is strictly better than not loading the page.
 */
function readStoredTheme(): GapReviewTheme | null {
  try {
    const storedTheme = window.localStorage.getItem(THEME_STORAGE_KEY);
    return storedTheme === 'dark' || storedTheme === 'light'
      ? storedTheme
      : null;
  } catch {
    return null;
  }
}

function readInitialTheme(): GapReviewTheme {
  const storedTheme = readStoredTheme();
  if (storedTheme) return storedTheme;
  return window.matchMedia('(prefers-color-scheme: light)').matches
    ? 'light'
    : 'dark';
}

const initialTheme = readInitialTheme();

/**
 * Light/dark for the gap-review surface only.
 *
 * The theme lives on the layout's own `.gr-root` element rather than on `<html>`
 * — the rest of the app has no dark mode, and this page is not the place to
 * introduce one.
 */
export function useGapReviewTheme(): {
  theme: GapReviewTheme;
  toggleTheme: () => void;
} {
  const [theme, setTheme] = useState<GapReviewTheme>(initialTheme);

  const toggleTheme = useCallback(() => {
    const nextTheme: GapReviewTheme = theme === 'dark' ? 'light' : 'dark';
    try {
      window.localStorage.setItem(THEME_STORAGE_KEY, nextTheme);
    } catch {
      // A theme that does not survive a reload is still worth having.
    }
    setTheme(nextTheme);
  }, [theme]);

  return { theme, toggleTheme };
}
